import { api } from "@/lib/api";

const CHUNK = 8 * 1024 * 1024;
const CHUNK_LANES = 4;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function withRetry(fn, tries = 5) {
  for (let i = 0; ; i++) {
    try { return await fn(); }
    catch (e) {
      const code = e?.response?.status;
      if (e?.name === "CanceledError" || (code && code < 500 && code !== 408 && code !== 429) || i >= tries - 1) throw e;
      await sleep(1000 * 2 ** i);
    }
  }
}

// Resumable upload: server tells us which chunks it already has, we send the rest.
export async function uploadFile(projectId, file, onProgress, signal, shouldPause) {
  const s = await withRetry(() => api.uploadInit(projectId, {
    filename: file.webkitRelativePath || file.name, size: file.size, chunk_size: CHUNK,
    content_type: file.type || "application/octet-stream",
  }));
  if (s.status !== "uploading") return { ...s, resumed: true };
  const have = new Set(s.received);
  const todo = Array.from({ length: s.total_chunks }, (_, i) => i).filter((i) => !have.has(i));
  let sent = have.size;
  onProgress(sent / s.total_chunks, 0);
  // several chunks in flight per file: hides per-request latency, which dominates over long links
  const lane = async () => {
    for (let i = todo.shift(); i !== undefined; i = todo.shift()) {
      while (shouldPause()) { if (signal.aborted) throw new Error("aborted"); await sleep(500); }
      const blob = file.slice(i * s.chunk_size, Math.min(file.size, (i + 1) * s.chunk_size));
      await withRetry(() => api.uploadChunk(s.id, i, blob, signal));
      sent += 1;
      onProgress(sent / s.total_chunks, blob.size);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CHUNK_LANES, todo.length) }, lane));
  return withRetry(() => api.uploadComplete(s.id));
}
